import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { PageSpinner } from '@/components/ui/Spinner'
import { useStoreSupplyItems } from '@/hooks/useBuyers'
import { useAuthStore } from '@/stores/authStore'
import type { MenuItemStatus } from '@/types/app'

const STATUS_BADGE: Record<MenuItemStatus, { label: string; className: string }> = {
  active:       { label: 'Active',       className: 'bg-green-100 text-green-700' },
  seasonal:     { label: 'Seasonal',     className: 'bg-amber-100 text-amber-700' },
  eighty_sixed: { label: '86\'d',        className: 'bg-red-100 text-red-700' },
  retired:      { label: 'Retired',      className: 'bg-stone-100 text-stone-500' },
}

export function StoreSupplyPage() {
  const { data: items, isLoading } = useStoreSupplyItems()
  const role = useAuthStore(s => s.user?.role)
  const showWholesale = role === 'owner' || role === 'ops'

  if (isLoading) return <PageSpinner />

  const all = items ?? []

  return (
    <div className="p-6 space-y-6">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Store Supply</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            Products available for wholesale / store supply orders
          </p>
        </div>
        <Link
          to="/products"
          className="inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium"
        >
          Manage in Products
          <ExternalLink size={14} />
        </Link>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                UPC Code
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Product Name
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Telugu Name
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Counter Price
              </th>
              {showWholesale && (
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Wholesale Price
                </th>
              )}
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {all.length === 0 ? (
              <tr>
                <td
                  colSpan={showWholesale ? 6 : 5}
                  className="px-4 py-12 text-center text-stone-400"
                >
                  No store supply products yet. Mark menu items as available for store supply in Menu &rarr; item &rarr; Details.
                </td>
              </tr>
            ) : (
              all.map(item => {
                const badge = STATUS_BADGE[item.status]
                return (
                  <tr
                    key={item.id}
                    className="border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {item.upc_code ? (
                        <span className="font-mono text-stone-700 text-xs">{item.upc_code}</span>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-stone-900">{item.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      {item.regional_name ? (
                        <span className="text-stone-500 italic">{item.regional_name}</span>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-800 font-medium">
                      ${item.price.toFixed(2)}
                    </td>
                    {showWholesale && (
                      <td className="px-4 py-3 text-stone-800 font-medium">
                        {item.wholesale_price != null
                          ? `$${item.wholesale_price.toFixed(2)}`
                          : <span className="text-stone-300">—</span>
                        }
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
